import React, { createRef, forwardRef, Fragment, KeyboardEvent, ReactNode, RefObject, useEffect, useState } from "react";
import { ScrollEventCallback, SizeChangedCallback } from "overlayscrollbars";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";

import { bemClassNames, combineRefs, Key } from "@multiweb/reactUtils";
import { ForwardRefType } from "@multiweb/types/react";
import { ScrollContext, ScrollContextType } from "../../context";
import { PADDING_EXTENSION_NAME, registerPaddingExtension } from "./utils";

/**
 * Dostupná témata pro stylování scrollbaru.
 */
export enum ThumbTheme {
    /** Posuvník má tmavě šedou barvu. Předdefinované téma knihovny overlayscrollbars */
    dark = "os-theme-dark",
    /** Posuvník má světle šedou barvu. Předdefinované téma knihovny overlayscrollbars */
    light = "os-theme-light",
}

export type MenuScrollLayoutComponentProps = {
    /** Obalovaná komponenta. */
    children: ReactNode;
    /** Fixní kladná výška rozložení v pixelech. */
    height?: number;
    /** Příznak, zda je aktivní horizontální scrollování. */
    horizontal?: boolean;
    /** Příznak, zda je aktivní vertikální scrollování. */
    vertical?: boolean;
    /** Fixní kladná šířka rozložení v pixelech. */
    width?: number;
    /** Téma posuvníků. */
    thumbTheme?: ThumbTheme;
    /** Callback pro událost scrollování. */
    onScroll?: ScrollEventCallback;
    /** Callback volaný při změně velikosti obsahu.  */
    onContentSizeChanged?: SizeChangedCallback;
    /** Obsah hlavičky. */
    stickyHeaderContent?: ReactNode;
    /** Obsah patičky. */
    stickyFooterContent?: ReactNode;
    /**
     * Příznak, zda se mají scrollbary zobrazit v odsazeném prostoru místo překrývání komponent.
     * Odsazení se přidá pouze, pokud je scrollbar zobrazen. Tuto prop nelze měnit dynamicky, aplikuje
     * se pouze jednou, při vytvoření komponenty. Její následné změny nemají žádný vliv.
     */
    usePaddedScrollbars?: boolean;
    /** Příznak indikující, zda se jedná o použití pro virtualizovaný list. Při aktivaci obalí obsah komponentami, které umožní získat rozměr pro AutoSizer. */
    inVirtualizedList?: boolean;
};

const classNames = bemClassNames("menu-scroll-layout-component");

const OVERFLOW_HIDDEN = "hidden";
const OVERFLOW_SCROLL = "scroll";

registerPaddingExtension();

/** Rozložení umožňující scrollování obsahu a nastavení fixní velikosti. Podporuje sticky hlavičku/patičku. */
export const MenuScrollLayoutComponent: ForwardRefType<OverlayScrollbarsComponent, MenuScrollLayoutComponentProps> = forwardRef<OverlayScrollbarsComponent, MenuScrollLayoutComponentProps>(({
    children,
    height,
    horizontal = true,
    vertical = true,
    width,
    thumbTheme = ThumbTheme.light,
    onScroll,
    onContentSizeChanged,
    stickyHeaderContent,
    stickyFooterContent,
    usePaddedScrollbars = false,
    inVirtualizedList = false,
}, forwardRef) => {
    const innerRef = createRef<OverlayScrollbarsComponent>();
    const ref = combineRefs(innerRef, forwardRef);
    const [scroll, setScroll] = useState<ScrollContextType>(null);
    useEffect(() => {
        setScroll(() => getScroll(innerRef));
        // TODO [MB] - Opravit hook deps v rámci 30853.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <div
            style={{ height, width }}
            className={classNames()}
        >
            {stickyHeaderContent && (
                <div className={classNames("sticky-header")} >
                    {stickyHeaderContent}
                </div>
            )}
            <OverlayScrollbarsComponent
                style={{ height, width }}
                className={classNames("custom-scroll", { vertical, horizontal }, [`${thumbTheme}`])}
                options={{
                    callbacks: { onScroll, onContentSizeChanged },
                    overflowBehavior: {
                        x: horizontal ? OVERFLOW_SCROLL : OVERFLOW_HIDDEN,
                        y: vertical ? OVERFLOW_SCROLL : OVERFLOW_HIDDEN,
                    },
                }}
                extensions={usePaddedScrollbars ? PADDING_EXTENSION_NAME : undefined}
                onKeyDown={preventDefaultScroll}
                ref={ref}
            >
                {inVirtualizedList ? (
                    <div className={classNames("scrollable-container")} >
                        {stickyHeaderContent && renderInnerStickyHeader(stickyHeaderContent)}
                        <div className={classNames("content-container")} >
                            <ScrollContext.Provider value={scroll}>
                                {children}
                            </ScrollContext.Provider>
                        </div>
                        {stickyFooterContent && renderInnerStickyFooter(stickyFooterContent)}
                    </div>
                ) : (
                    <Fragment>
                        {renderInnerStickyHeader(stickyHeaderContent)}
                        <ScrollContext.Provider value={scroll}>
                            {children}
                        </ScrollContext.Provider>
                        {renderInnerStickyFooter(stickyFooterContent)}
                    </Fragment>
                )}
            </OverlayScrollbarsComponent>
            {stickyFooterContent && (
                <div className={classNames("sticky-footer")}>
                    {stickyFooterContent}
                </div>
            )}
        </div>
    );
});

/**
 * Získá scroll funkci z OverlayScrollbarsComponent, pokud to je možné.
 * @param ref Objekt s referencí na komponentu.
 */
const getScroll = (ref: RefObject<OverlayScrollbarsComponent>) => {
    if (ref && ref.current) {
        const instance = ref.current.osInstance();
        return instance ? instance.scroll : null;
    } else {
        return null;
    }
};

const renderInnerStickyHeader = (stickyHeaderContent?: ReactNode) => (
    <div className={classNames("inner-sticky-header")} >
        {stickyHeaderContent}
    </div>
);

const renderInnerStickyFooter = (stickyFooterContent?: ReactNode) => (
    <div className={classNames("inner-sticky-footer")}>
        {stickyFooterContent}
    </div>
);

const SUPPRESSED_KEYS: readonly string[] = [Key.ArrowUp, Key.ArrowDown, Key.ArrowLeft, Key.ArrowRight];

/**
 * Zakáže výchozí chování scrollbarů pro klávesy šipek. O posunutí scrollbaru se stará useSetFocus hook při změně zaměřené položky.
 */
const preventDefaultScroll = (event: KeyboardEvent<HTMLDivElement>) => {
    if (SUPPRESSED_KEYS.includes(event.key)) {
        event.preventDefault();
    }
};
